import React, { useState, useEffect } from 'react';

const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if we're in a local file system (file://) which doesn't support cookies
    if (window.location.protocol === 'file:') {
      console.warn('Cookies are not supported when running from file:// protocol');
      // Use localStorage as fallback
      const consent = localStorage.getItem('cookie-consent');
      if (!consent) {
        const timer = setTimeout(() => {
          setShowBanner(true);
        }, 1000);
        return () => clearTimeout(timer);
      }
    } else {
      const consent = getCookie('cookie-consent');
      // If cookie not found, check localStorage as fallback
      const localConsent = localStorage.getItem('cookie-consent');
      if (!consent && !localConsent) {
        // Show the banner with a slight delay for better UX
        const timer = setTimeout(() => {
          setShowBanner(true);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, []);

  const setCookie = (name: string, value: string, days: number) => {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = '; expires=' + date.toUTCString();
    }
    // Set secure cookies with proper SameSite attribute
    // Try setting cookie without domain first
    const cookieString = name + '=' + (value || '') + expires + '; path=/';
    document.cookie = cookieString;
  };

  const getCookie = (name: string): string | null => {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length, c.length);
      }
    }
    return null;
  };

  const handleAccept = () => {
    // Try to set cookie
    setCookie('cookie-consent', 'all', 365);
    // Also set in localStorage as fallback
    try {
      localStorage.setItem('cookie-consent', 'all');
    } catch (e) {
      console.error('Failed to save preference:', e);
    }
    setShowBanner(false);
  };

  const handleDecline = () => {
    // Try to set cookie
    setCookie('cookie-consent', 'essential', 365);
    // Also set in localStorage as fallback
    try {
      localStorage.setItem('cookie-consent', 'essential');
    } catch (e) {
      console.error('Failed to save preference:', e);
    }
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-6 left-6 max-w-md bg-white rounded-lg shadow-xl z-50 border border-gray-200">
      <div className="p-6">
        <div className="mb-4">
          <h3 className="text-xl font-bold mb-2">We Use Cookies</h3>
          <p className="text-sm text-gray-600">
            This website uses cookies to ensure you get the best experience on our website. 
            By continuing to use this site, you consent to our use of cookies in accordance 
            with our{' '}
            <button 
              type="button"
              onClick={() => window.alert('Cookie Policy - We use cookies to improve your experience on our website.')}
              className="text-blue-600 hover:underline bg-transparent border-0 cursor-pointer p-0"
            >
              Cookie Policy
            </button>.
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={handleAccept}
            className="px-6 py-2 bg-blue-600 text-white rounded-full font-semibold text-sm hover:bg-blue-700 transition"
          >
            Accept All
          </button>
          <button
            onClick={handleDecline}
            className="px-6 py-2 bg-transparent border border-gray-300 text-gray-700 rounded-full font-semibold text-sm hover:bg-gray-100 transition"
          >
            Essential Only
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;